/**
 * Plain-word display of the patient's payment row. Never a Stripe term.
 *
 * The fee notice's wording is the server's (`notice.text_blocks`); these are
 * the link's own short lines around it: the amount, the one-liner on the
 * status rail and the done screen, and the card-saved strip, whose sentence
 * must match the notice's "charged right before your form is sent to the
 * insurer" (G21, 2026-10-15, DRAFT: the card is charged just before the send
 * and the form goes only if the charge did).
 *
 * No free path (ruling 2026-10-14, case imp_a6b6fd85): a row frozen without
 * the no-fee choices (`no_fee_choices` not true, every row the Mac sends
 * today) offers two ways out, "Continue to payment" and "I have decided not
 * to have this form filled out" (Michael, 2026-10-15, case imp_e56c2f19:
 * "they can't complete the form themselves", so the decline never sends the
 * patient to a paper form). The decline's own lines are below (`DECLINE_*`,
 * `declinedCopy`). EVERY sentence added for the ruling is a DRAFT for
 * Michael's approval before live.
 */
import type { PatientPaymentStatus } from "../types/shared";

/** 4500 → "$45"; 4550 → "$45.50". CAD; the currency is stated by the notice. */
export function formatCents(cents: number): string {
  const n = Math.max(0, Math.round(Number(cents) || 0));
  const dollars = Math.floor(n / 100);
  const rest = n % 100;
  return rest === 0 ? `$${dollars}` : `$${dollars}.${String(rest).padStart(2, "0")}`;
}

/** "Dr. Mindrum" stays as is; "Michael Mindrum" becomes "Dr. Michael Mindrum". */
export function physicianName(displayName: string | null | undefined): string {
  const name = (displayName || "").trim();
  if (!name) return "Your doctor";
  return /^dr\b\.?/i.test(name) ? name : `Dr. ${name}`;
}

/** Who sells the fee. `physician`: the signing physician's practice charges
 *  it (a Connect charge). `parseo`: Parseo Health is the seller (the Model A
 *  pilot) and the physician reviews and signs at no charge. */
export type FeeMerchant = "physician" | "parseo";

export const PARSEO_SELLER_NAME = "Parseo Health";
/** Parseo's contact for fee questions. There is no Parseo phone constant yet;
 *  when one exists it belongs here, beside the email. */
export const PARSEO_CONTACT_EMAIL = "finance@parseohealth.com";

/** The row's seller, never optional on this side: the bridge always sends
 *  `merchant`, and a row without one (or with anything but exactly "parseo")
 *  reads as the physician, the only seller before Model A. The bridge's own
 *  `_merchant(row)` makes the same call. */
export function feeMerchant(p: Pick<PatientPaymentStatus, "merchant"> | null | undefined): FeeMerchant {
  return p?.merchant === "parseo" ? "parseo" : "physician";
}

/** How the patient reaches whoever decides on the fee. */
export interface FeeContact {
  kind: "phone" | "email";
  value: string;
  href: string;
}

/** The fee step's chrome around the server's notice: who charges, who a
 *  waiver or a question goes to, and how to reach them. */
export interface FeeSeller {
  merchant: FeeMerchant;
  /** The signing physician: "Dr. Testdoc" (or "Your doctor"). */
  doctor: string;
  /** Who sees a waiver request or a question: "Dr. Testdoc's office", or
   *  "Parseo Health" when Parseo sells the fee. */
  office: string;
  /** The line under the heading while the server's notice shows below it:
   *  "Dr. Testdoc · form-completion fee", or "Parseo Health charges this
   *  fee." (the Parseo notice itself says the physician signs at no charge). */
  byline: string;
  /** The same line when the notice is NOT shown (pay mode with a current
   *  consent): for Parseo it adds "Dr. Testdoc reviews and signs your form
   *  at no charge.", which the hidden notice would otherwise have said. The
   *  physician's line is the same either way. */
  bylineWithoutNotice: string;
  /** The practice phone (physician), or Parseo's email (parseo). A Parseo
   *  row never shows `practice_phone`: that number is a practice's, and the
   *  practice is not who the patient pays. */
  contact: FeeContact | null;
}

export function feeSeller(
  p: Pick<PatientPaymentStatus, "merchant" | "physician_display_name" | "practice_phone">,
): FeeSeller {
  const doctor = physicianName(p.physician_display_name);
  if (feeMerchant(p) === "parseo") {
    return {
      merchant: "parseo",
      doctor,
      office: PARSEO_SELLER_NAME,
      byline: `${PARSEO_SELLER_NAME} charges this fee.`,
      bylineWithoutNotice: `${PARSEO_SELLER_NAME} charges this fee. ${doctor} reviews and signs your form at no charge.`,
      contact: { kind: "email", value: PARSEO_CONTACT_EMAIL, href: `mailto:${PARSEO_CONTACT_EMAIL}` },
    };
  }
  const phone = (p.practice_phone || "").trim();
  const byline = `${doctor} · form-completion fee`;
  return {
    merchant: "physician",
    doctor,
    office: `${doctor}'s office`,
    byline,
    bylineWithoutNotice: byline,
    contact: phone ? { kind: "phone", value: phone, href: `tel:${phone.replace(/[^\d+]/g, "")}` } : null,
  };
}

/** " You can also call Dr. Testdoc's office at 902-555-0100." /
 *  " You can also email Parseo Health at finance@parseohealth.com." / "". */
export function contactSentence(s: FeeSeller): string {
  if (!s.contact) return "";
  const verb = s.contact.kind === "email" ? "email" : "call";
  return ` You can also ${verb} ${s.office} at ${s.contact.value}.`;
}

export const CARD_SAVED_SENTENCE = "is charged right before your form is sent to the insurer.";

/** "Oct 19" — the day a card was saved, in the reader's time zone; "" when
 *  the bridge did not say (an older bridge) or the stamp does not parse. */
export function cardSavedDay(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

/** The strip above the questionnaire when the card is already on file. It
 *  says WHEN: a re-sent request keeps the card from its first send, and
 *  "Card saved." alone read as a card the patient never entered (Michael,
 *  2026-10-28). */
export function cardSavedStrip(p: Pick<PatientPaymentStatus, "total_cents" | "card_saved_at">): string {
  const day = cardSavedDay(p.card_saved_at);
  return `Card saved${day ? ` on ${day}` : ""}. ${formatCents(p.total_cents)} ${CARD_SAVED_SENTENCE}`;
}

/** The Fee row on the status rail and the one-liner on the done screen, or
 *  null when the row's status has nothing the patient needs to see there. */
export function feeRowLabel(
  p: Pick<PatientPaymentStatus, "status" | "total_cents"> | null | undefined,
): { label: string; done: boolean } | null {
  if (!p) return null;
  switch (p.status) {
    case "card_saved":
      return { label: "Card saved · charged right before sending", done: true };
    case "paid":
      return { label: `Paid ${formatCents(p.total_cents)} · receipt sent`, done: true };
    case "waived":
      return { label: "Fee waived", done: true };
    case "charge_failed":
      return { label: "Payment failed · you can pay from your link", done: false };
    case "deferred":
      return { label: "Fee not yet paid · pay from this link", done: false };
    case "declined_old_way":
      // DRAFT (2026-10-15, G20).
      return { label: "You decided not to have this form filled out", done: true };
    default:
      return null;
  }
}

/** Whether this row offers the patient's no-fee choices ("I can't pay right
 *  now", "I have a question", "Not now"). Only an explicit `true` from the
 *  bridge, frozen per row from the Mac's push; absent (an older bridge) or
 *  false is the two-choice fee step. The bridge refuses those routes on such
 *  a row (409 `no_fee_choices_off`) whatever the page shows. */
export function noFeeChoices(p: Pick<PatientPaymentStatus, "no_fee_choices"> | null | undefined): boolean {
  return p?.no_fee_choices === true;
}

// ── "I have decided not to have this form filled out" (G20) ────────────────
//
// Michael, 2026-10-15 (case imp_e56c2f19): "don't agree re: patients choice.
// they can't complete the form themselves." The decline closes the request:
// nothing is charged, Parseo prepares and sends nothing, and the signing
// physician's office is told (or Parseo Health, when no notice reaches the
// office: `declineToldTo`). No paper form, no printing, no "complete it
// yourself". All DRAFT, for Michael's approval before live.

/** The second button on the two-choice fee step. The SAME words the hashed
 *  notice names (`DECLINE_SENTENCE` / `PARSEO_DECLINE_SENTENCE` in
 *  med_form_app/backend/payment_templates.py, character for character;
 *  pinned by tests/bridge/test_payments_contract.py), so the button the
 *  patient taps is the one the notice they consented to describes. */
export const DECLINE_BUTTON_LABEL = "I have decided not to have this form filled out";
export const DECLINE_CONFIRM_HEADING = "Not have this form filled out?";
export const DECLINE_CONFIRM_YES = "Yes, I've decided";
export const DECLINED_HEADING = "Your choice has been recorded";
/** Above the fee step when the bridge refused the form for its fee (409
 *  `fee_required`: a stale page, or the fee check could not run). */
export const FEE_REQUIRED_LINE =
  "Your form is sent once a card is saved, or you can decide not to have it filled out.";
/** A plan member (or any signer but the patient) on a request the patient
 *  closed. Neutral on purpose: it never says why. */
export const REQUEST_CLOSED_HEADING = "This request was closed.";
export const REQUEST_CLOSED_BODY = "Nothing more is needed from you.";

// ── The patient's fee-only link (G23(f), Michael approved 2026-10-16) ──────
//
// A form with nothing for the patient to fill, which only the plan holder
// signs. The patient's own link shows the fee step and nothing else: the
// server's notice (the same hashed notice), card entry and the decline. No
// details to confirm, no form, nothing to sign. When the fee step is done the
// page says so with the lines the fee step already uses, and that nothing
// more is needed ONLY when that is true: a card saved or the fee paid (review
// of PR #535, F5). A fee step that cannot run (paused, not configured) never
// reaches the closing page: the page holds on the existing "We couldn't open
// your form just now" view with its retry. DRAFT, for Michael's approval.

/** Pay mode's line on a card declined right before the send (FeeStage), and
 *  the fee-only closing page's after "Not now" on it. */
export const EARLIER_PAYMENT_FAILED_LINE =
  "The earlier payment didn't go through. You can pay with a card on the payment page.";

/** The closing page of a fee-only link, by the row as the bridge last
 *  returned it. `nothingMore`: say "Nothing more is needed from you."; only
 *  a saved card or a paid fee. Every other line is one the fee step or the
 *  status rail already uses for that state. */
export function feeOnlyDoneCopy(
  p: Pick<PatientPaymentStatus, "status" | "total_cents"> | null | undefined,
): { heading: string; body: string; nothingMore: boolean } {
  if (p?.status === "card_saved") {
    return { heading: "Your card is saved", body: `${formatCents(p.total_cents)} ${CARD_SAVED_SENTENCE}`,
             nothingMore: true };
  }
  if (p?.status === "paid") {
    return { heading: "Your fee is paid", body: feeRowLabel(p)?.label || "", nothingMore: true };
  }
  const row = feeRowLabel(p);
  if (p?.status === "charge_failed") {
    // "Not now" on the pay-now screen: the fee still waits on the patient.
    return { heading: row?.label || "", body: EARLIER_PAYMENT_FAILED_LINE, nothingMore: false };
  }
  if (row) return { heading: row.label, body: "", nothingMore: false };
  // A fee that ended another way (the request closed: cancelled, refunded).
  return { heading: REQUEST_CLOSED_HEADING, body: "", nothingMore: false };
}

/** The signing physician's office, which is told of the decline: "Dr.
 *  Testdoc's office", or "your doctor's office" when the row names no one.
 *  The same office whoever sells the fee (Model A and a $0 physician
 *  included). */
export function signingOffice(p: Pick<PatientPaymentStatus, "physician_display_name"> | null | undefined): string {
  const name = (p?.physician_display_name || "").trim();
  return name ? `${physicianName(name)}'s office` : "your doctor's office";
}

/** The office's own phone, when the row carries one. The Mac sends
 *  `practice_phone` only on a physician-sold row, and only the office's own
 *  number (never Parseo's), so a Parseo-sold row has none and the office is
 *  named without a number. */
export function signingOfficePhone(p: Pick<PatientPaymentStatus, "practice_phone"> | null | undefined): FeeContact | null {
  const phone = (p?.practice_phone || "").trim();
  return phone ? { kind: "phone", value: phone, href: `tel:${phone.replace(/[^\d+]/g, "")}` } : null;
}

function _cap(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/** Whom the decline reaches, as the patient is told (review of PR #517): the
 *  office is named only when a notice actually goes there. The row's
 *  `decline_told` (frozen by the Mac): `office` → "Dr. Testdoc's office" (the
 *  physician the row names is belled: a physician-sold row, a physician who
 *  charges $0, a Model A signer with a portal account); `parseo` → "Parseo
 *  Health" (a Model A row whose signer has no portal account: only the
 *  operator hears). A row older than the field reads by seller, and no row
 *  at all (the page learned of the decline from a refusal) says Parseo
 *  Health, the one party every decline reaches. */
export function declineToldTo(p: PatientPaymentStatus | null | undefined): string {
  const told = p?.decline_told;
  if (told === "office") return _cap(signingOffice(p));
  if (told === "parseo" || !p) return PARSEO_SELLER_NAME;
  return feeMerchant(p) === "parseo" ? PARSEO_SELLER_NAME : _cap(signingOffice(p));
}

/** The confirm screen's paragraph, before anything is recorded: Parseo (or
 *  Parseo Health, when it sells the fee) prepares and sends nothing, nothing
 *  is charged, and whoever the decline reaches will be told. */
export function declineConfirmBody(p: PatientPaymentStatus | null | undefined): string {
  const who = p && feeMerchant(p) === "parseo" ? PARSEO_SELLER_NAME : "Parseo";
  return (
    `${who} won't prepare or send this form, and you won't be charged. ` +
    `${declineToldTo(p)} will be told that you decided not to have it filled out.`
  );
}

/** The closing page (and the status page's card) after the decline: nothing
 *  was charged, and whoever the decline reaches will be told ("will": the
 *  bell rings when the Mac next pulls the bridge's event). No
 *  questionnaire, no tracker, no "what happens next", no paper-form or
 *  printing instructions, and never the prepared PDF. `phone` is the
 *  office's own number, when the row has one. */
export function declinedCopy(p: PatientPaymentStatus | null | undefined): {
  heading: string;
  body: string;
  office: string;
  phone: FeeContact | null;
} {
  return {
    heading: DECLINED_HEADING,
    body:
      `Nothing has been charged. ${declineToldTo(p)} will be told that you decided ` +
      `not to have this form filled out.`,
    office: _cap(signingOffice(p)),
    phone: signingOfficePhone(p),
  };
}

/** A decline the bridge refused (409 `not_declinable`) on a row still
 *  waiting on its fee (`pending` or `card_saved`): the patient's form is
 *  already in, a send is locked in, or the fee was already released. The
 *  notice's cancellation sentence is the way out: through the office. Only
 *  for those two statuses; a refusal carrying any other is
 *  `requestChangedLine`'s. */
export function notDeclinableLine(p: PatientPaymentStatus | null | undefined): string {
  const phone = signingOfficePhone(p);
  return (
    `Your form is already on its way, so this choice can no longer be made here. ` +
    `To cancel it, contact ${signingOffice(p)}${phone ? ` at ${phone.value}` : ""}.`
  );
}

/** The request changed under an open page: the bridge refused the decline
 *  or the pay page because the row no longer waits on its fee (the
 *  physician closed the request, the fee was waived, …). The page does not
 *  know which, so it says neither "on its way" nor "try again": it sends the
 *  patient to their link, which reads the request afresh, or to the office.
 *  DRAFT (2026-10-15, review of PR #508). */
export function requestChangedLine(p: PatientPaymentStatus | null | undefined): string {
  const phone = signingOfficePhone(p);
  return (
    `This request has changed. Open your link again, or contact ` +
    `${signingOffice(p)}${phone ? ` at ${phone.value}` : ""}.`
  );
}

/** A decline that did not reach the bridge: nothing is recorded, so the page
 *  stays on the confirm screen and the patient tries again. */
export function declineFailedLine(s: FeeSeller): string {
  return `We couldn't record your choice just now. Please try again in a moment.${contactSentence(s)}`;
}

/** Only a `pending`, unpaused row shows the fee step. Every other answer —
 *  404, 503, an error, or any other status — goes straight to the form: a
 *  fee screen is never the fallback. */
export function feeStepApplies(p: Pick<PatientPaymentStatus, "status" | "paused"> | null | undefined): boolean {
  return !!p && !p.paused && p.status === "pending";
}

/** Rows the patient can pay later from their link: save a card (`deferred`)
 *  or pay now (`charge_failed`, a payment-mode Checkout). */
export function canPayFromLink(p: Pick<PatientPaymentStatus, "status" | "paused"> | null | undefined): boolean {
  return !!p && !p.paused && (p.status === "deferred" || p.status === "charge_failed");
}

/** The session roles the payment endpoints serve — the server's rule
 *  mirrored: `legacy`, `patient`, or no role (older sessions). Never the plan
 *  member or the prescriber; the link does not even ask for them. */
export function paymentRoleAllowed(role: unknown): boolean {
  return role === undefined || role === null || role === "" || role === "legacy" || role === "patient";
}

export const DEFERRED_MESSAGE = "Your form continues. You can pay the fee later from this link.";

/** The text the consent hash covers, exactly as rendered: the notice blocks
 *  then the consent sentence, joined by blank lines. */
export function consentHashInput(notice: { text_blocks?: string[]; consent_text?: string } | null | undefined): string {
  return [...(notice?.text_blocks || []), notice?.consent_text || ""].join("\n\n");
}
